import React, { useState } from 'react';
import { ImageField, LinkField } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import { Components } from '.generated/templates/Feature.Components.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Routes } from '.generated/templates/Project.Routes.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import ArroweExpand from 'components/helpers/SvgIcon/icons/arrow-expand';
import ArrowCollapse from 'components/helpers/SvgIcon/icons/arrow-collape';
import { useScreenBreakpoint } from 'lib/util/useScreenWidth';
import { IsNetworkSite } from 'lib/util/site-configuration';
import {
  alwaysSuppressLinkDataLayerPush,
  pageNavigationDataLayerHandler,
} from 'lib/util/gtm-datalayer';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';

export type SocialLinks = Sitecore.Override.ItemEx & Data.SocialLinks.Fields.SocialLinks;
export type GenericPage = Sitecore.Override.ItemEx & Routes.Fields.GenericPage;
export type NavigationLink = Sitecore.Override.ItemEx & Data.Navigation.Fields.NavigationLink;
export type pageorLink = GenericPage | NavigationLink;

type navigationFooter = Sitecore.Override.ItemEx &
  Data.Navigation.Fields.NavigationFooter & { children?: NavigationLink[] };

export type NrcFooterProps = {
  fields: {
    root?: Components.Site.Fields.Footer & {
      footerLinks?: pageorLink[];
      children?: navigationFooter[];
      socialLinks?: SocialLinks[];
    };
  };
};

const NrcFooter = ({ fields }: NrcFooterProps): JSX.Element => {
  const { isMediumScreen } = useScreenBreakpoint();

  if (!fields || fields?.root?.fields?.disclaimer === undefined) {
    return <MissingDataSource />;
  }
  const renderSocialSection = fields?.root?.fields?.socialLinks?.length > 0 ? true : false;
  const footerContainer = classnames('relative', 'pt-2', 'md:pt-10', 'text-center');
  const hoverNoUnderline = classnames(`hover:no-underline`);
  const maxWidthScreen2Xl = classnames(`max-w-screen-2xl`);
  const textBlueNight = classnames(`text-blue-night`);

  const IsNetwork = IsNetworkSite() ? true : false;

  const SocialLinkList = fields?.root?.fields?.socialLinks?.map((item) => {
    const slink = item as SocialLinks;
    const iconField = item.fields.icon
      ? {
          ...item.fields.icon,
          value: {
            width: '24',
            height: '24',
            ...(item.fields.icon as ImageField).value,
          },
        }
      : undefined;
    return (
      <li key={slink.id} className={classnames('px-0', 'px-2', 'md:first:pl-0')}>
        <LinkA11yWrapper
          className={classnames(
            'box-content',
            'block',
            'pt-2',
            'pb-1',
            'sm:pt-4',
            'sm:pb-3.5',
            'max-h-[44px]',
            'max-w-[44px]',
            'min-h-[44px]',
            'min-w-[44px]'
          )}
          editable={false}
          field={slink.fields.navigationItemLink}
          id={`social-links--${slink.id}`}
          rel="noreferrer noopener"
          suppressLinkText
          suppressNewTabIcon
          onGtmPush={alwaysSuppressLinkDataLayerPush}
          data-gtm-interaction-type="click: social"
          data-gtm-title={slink.displayName}
        >
          {iconField && (
            <ImageWrapper
              layout="responsive"
              field={iconField}
              className={classnames('justify-center', 'w-full', 'h-full')}
            />
          )}
        </LinkA11yWrapper>
      </li>
    );
  });

  const footerLinkList = fields?.root?.fields?.footerLinks?.map((item) => {
    const navitem = item.fields.navigationItemLink ? (item as NavigationLink) : null;
    const pageitem = !item.fields.navigationItemLink ? (item as GenericPage) : null;
    const linkfield = navitem
      ? navitem.fields.navigationItemLink
      : {
          value: {
            href: pageitem?.url,
            text:
              pageitem?.fields.navigationTitle.value ||
              pageitem?.fields.pageTitle.value ||
              pageitem?.name,
          },
        };
    return (
      <React.Fragment key={item.id}>
        <LinkA11yWrapper
          className={classnames(
            'border-0',
            'border-white',
            'font-regular',
            'h-3',
            hoverNoUnderline,
            'inline-block',
            'leading-[18px]',
            'md:leading-[10px]',
            'md:border-r',
            'md:ml-3',
            'ml-4',
            'mb-6',
            'md:my-2.5',
            'md:last:border-r-0',
            'pr-2.5',
            'underline',
            'whitespace-nowrap'
          )}
          editable={false}
          field={linkfield}
          onGtmPush={(e) => {
            pageNavigationDataLayerHandler(e);
          }}
        />
      </React.Fragment>
    );
  });

  const footerMainLinkList = fields?.root?.children?.map((item, index, array) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [isActive, setIsActive] = useState(false);
    return (
      <React.Fragment key={item.id}>
        <div
          className={classnames(
            'text-left',
            textBlueNight,
            'border-b',
            'md:border-0',
            'md:pr-4',
            'last:pr-0'
          )}
        >
          {isMediumScreen ? (
            <>
              <h2 className={classnames('text-xl', 'font-bold', 'pb-4', 'hidden', 'md:block')}>
                {item.displayName}
              </h2>
              <div className={classnames('pl-10', 'pt-2', 'md:pt-0', 'md:pl-0')}>
                {item.children?.map((item2) => (
                  <LinkA11yWrapper
                    key={item2.id}
                    className={classnames(
                      'flex',
                      'text-base',
                      'font-medium',
                      'pb-4',
                      'underline',
                      'leading-4',
                      hoverNoUnderline,
                      'hover:font-medium'
                    )}
                    editable={false}
                    field={item2.fields.navigationItemLink}
                    onGtmPush={(e) => {
                      pageNavigationDataLayerHandler(e);
                    }}
                  />
                ))}
              </div>
              {index === array.length - 1 ? (
                <>
                  {renderSocialSection && (
                    <ul
                      className={classnames(
                        'flex',
                        'flex-wrap',
                        'md:p-0',
                        'pb-3',
                        'pl-0',
                        'justify-start',
                        'pl-8'
                      )}
                    >
                      {SocialLinkList}
                    </ul>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                className={classnames(
                  'block',
                  'flex',
                  'font-semibold',
                  'md:hidden',
                  'md:mt-0',
                  'pb-2',
                  'pt-8',
                  'text-left',
                  'w-full'
                )}
                onClick={() => setIsActive(!isActive)}
              >
                <span className={classnames('pt-1', 'pr-4')}>
                  {isActive ? (
                    <ArroweExpand className={textBlueNight} />
                  ) : (
                    <ArrowCollapse className={textBlueNight} />
                  )}
                </span>
                <h2 className={classnames('text-xl', 'font-bold', 'leading-4')}>
                  {item.displayName}
                </h2>
              </button>
              {isActive && (
                <>
                  <div className={classnames('pl-10', 'pt-2', 'md:pt-0', 'md:pl-0')}>
                    {item.children?.map((item2) => (
                      <LinkA11yWrapper
                        key={item2.id}
                        className={classnames(
                          'flex',
                          'text-base',
                          'font-medium',
                          'pb-4',
                          'underline',
                          'leading-4',
                          hoverNoUnderline,
                          'hover:font-medium'
                        )}
                        editable={false}
                        field={item2.fields.navigationItemLink as LinkField}
                        onGtmPush={(e) => {
                          pageNavigationDataLayerHandler(e);
                        }}
                      />
                    ))}
                  </div>
                  {index === array.length - 1 ? (
                    <>
                      {renderSocialSection && (
                        <ul
                          className={classnames(
                            'flex',
                            'flex-wrap',
                            'md:p-0',
                            'pb-3',
                            'pl-0',
                            'justify-start',
                            'pl-8'
                          )}
                        >
                          {SocialLinkList}
                        </ul>
                      )}
                    </>
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
      </React.Fragment>
    );
  });

  return (
    <>
      <div data-component="authorable/site/footer">
        <div className={classnames(maxWidthScreen2Xl, 'mx-auto', 'pt-2')}>
          <hr
            className={classnames(maxWidthScreen2Xl, 'mx-auto', 'border-t-2', 'border-blue-night')}
          />
          <div className={footerContainer}>
            {IsNetwork ? (
              <div
                className={classnames(
                  'flex-row',
                  'justify-between',
                  maxWidthScreen2Xl,
                  'md:flex',
                  'md:pb-2',
                  'pb-0',
                  'px-4',
                  'md:px-20'
                )}
              >
                {footerMainLinkList}
              </div>
            ) : (
              <>
                {renderSocialSection && (
                  <>
                    <div
                      className={classnames(
                        'flex',
                        'flex-col',
                        'justify-center',
                        'items-center',
                        'md:mx-52',
                        'border-b',
                        'border-gray-manatee'
                      )}
                    >
                      <div
                        className={classnames(
                          'block',
                          'pb-1',
                          'sm:pb-2.5',
                          'text-blue-navy',
                          'text-xl',
                          'font-semibold'
                        )}
                      >
                        {fields?.root?.fields?.socialText.value}
                      </div>
                      <ul className={classnames('flex', 'flex-wrap', 'md:p-0', 'pb-3')}>
                        {SocialLinkList}
                      </ul>
                    </div>
                  </>
                )}
              </>
            )}

            <div className={classnames('mx-auto')}>
              <div
                className={classnames(
                  'mx-auto',
                  'md:border-b',
                  'border-0',
                  'border-gray-whisper',
                  'max-w-screen-xl',
                  'mx-auto'
                )}
              />
              <div
                className={classnames(
                  'leading-1',
                  'px-6',
                  'py-10',
                  'sm:py-6',
                  'text-black',
                  'text-left',
                  'lg:text-center',
                  'text-sm',
                  'md:px-20'
                )}
              >
                <p className={classnames('mb-3')}>{fields?.root?.fields?.disclaimer.value}</p>
                <p>
                  {fields?.root?.fields?.copyright.value.replaceAll(
                    '@year',
                    new Date().getFullYear().toString()
                  )}
                </p>
              </div>
              <div
                className={classnames(
                  'align-baseline',
                  'bg-blue-navy',
                  'flex',
                  'flex-col',
                  'flex-wrap',
                  'font-regular',
                  'md:block',
                  'md:text-center',
                  'pb-6',
                  'pt-4',
                  'sm:flex',
                  'sm:py-3.5',
                  'text-left',
                  'text-white',
                  'text-xs'
                )}
              >
                {footerLinkList}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NrcFooter;
